import { useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { toast } from "sonner";
import { DownloadSimple, MagnifyingGlass, ShieldCheck } from "@phosphor-icons/react";

export default function AdminPage() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyEmail, setBusyEmail] = useState("");
  const [q, setQ] = useState("");
  const [waitlist, setWaitlist] = useState([]);
  const [showWaitlist, setShowWaitlist] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [ru, rw] = await Promise.all([
        api.get("/admin/users"),
        api.get("/admin/waitlist").catch(() => ({ data: [] })),
      ]);
      setUsers(ru.data || []);
      setWaitlist(rw.data || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => {
    if (user && user.role === "admin") load();
  }, [user]);

  const updatePlan = async (email, plan) => {
    setBusyEmail(email);
    try {
      await api.patch(`/admin/users/${encodeURIComponent(email)}/plan`, { plan });
      toast.success(`${email} → ${plan}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusyEmail(""); }
  };

  const downloadCsv = async () => {
    try {
      const r = await api.get("/admin/users.csv", { responseType: "blob" });
      const blob = new Blob([r.data], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "brandloop-users.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return users;
    return users.filter(u =>
      (u.email || "").toLowerCase().includes(term) ||
      (u.name || "").toLowerCase().includes(term) ||
      (u.plan || "").toLowerCase().includes(term)
    );
  }, [users, q]);

  const totals = useMemo(() => ({
    all: users.length,
    trial: users.filter(u => u.plan === "trial").length,
    pro: users.filter(u => u.plan === "pro").length,
    archived: users.filter(u => u.plan === "archived").length,
  }), [users]);

  if (user && user.role !== "admin") return <Navigate to="/app" replace />;

  return (
    <div>
      <PageHeader
        eyebrow="Admin"
        title="Signups and account controls."
        subtitle="Every user who's ever touched BrandLoop, with their plan, activity, and quick controls. This page is admin-only."
        action={
          <button data-testid="admin-csv-btn" className="btn btn-accent" onClick={downloadCsv}>
            <DownloadSimple size={16}/> Export CSV
          </button>
        }
      />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        {[
          ["Total signups", totals.all],
          ["On Pro", totals.pro],
          ["On trial", totals.trial],
          ["Pro waitlist", waitlist.length],
        ].map(([label, val]) => (
          <div key={label} className="card p-5 fade-up" data-testid={label === "Pro waitlist" ? "admin-waitlist-count" : undefined} onClick={label === "Pro waitlist" ? () => setShowWaitlist((v)=>!v) : undefined} style={label === "Pro waitlist" ? { cursor: "pointer" } : {}}>
            <div className="eyebrow mb-2 flex items-center gap-1.5">
              {label === "Total signups" && <ShieldCheck size={11} weight="fill" style={{color:"var(--accent)"}}/>}
              {label}
              {label === "Pro waitlist" && waitlist.length > 0 && (
                <span className="text-[10px] font-mono ml-1" style={{color:"var(--accent-2)"}}>{showWaitlist ? "hide" : "view"}</span>
              )}
            </div>
            <div className="font-serif text-3xl tracking-tight font-semibold">{val.toLocaleString()}</div>
          </div>
        ))}
      </div>

      {showWaitlist && (
        <div className="card p-5 mb-6 fade-up" data-testid="admin-waitlist-panel">
          <div className="flex items-center justify-between mb-3">
            <div className="eyebrow">Pro waitlist · {waitlist.length}</div>
            <button onClick={()=>setShowWaitlist(false)} className="btn btn-ghost text-xs py-1 px-2.5">Close</button>
          </div>
          {waitlist.length === 0 ? (
            <div className="text-sm" style={{color:"var(--ink-3)"}}>Nobody has joined the Pro waitlist yet.</div>
          ) : (
            <div className="space-y-2 max-h-[400px] overflow-y-auto scroll-thin">
              {waitlist.map((w) => (
                <div key={w.id} data-testid={`waitlist-entry-${w.id}`} className="p-3 rounded-2xl flex items-start justify-between gap-3" style={{background:"var(--bg-soft)"}}>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium">{w.name || w.email}</div>
                    <div className="text-[11px] font-mono truncate" style={{color:"var(--ink-3)"}}>{w.email} · {w.use_case || "no use case"} · joined {new Date(w.created_at).toLocaleDateString(undefined,{month:'short',day:'numeric'})}</div>
                    {w.note && <div className="text-xs mt-1.5 italic" style={{color:"var(--ink-2)"}}>"{w.note}"</div>}
                  </div>
                  <button onClick={()=>updatePlan(w.email, "pro")} disabled={busyEmail === w.email} className="btn btn-accent text-xs py-1 px-3">Grant Pro</button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="card p-4 mb-4 flex items-center gap-3">
        <MagnifyingGlass size={16} style={{color:"var(--ink-3)"}}/>
        <input
          data-testid="admin-search"
          className="flex-1 bg-transparent outline-none text-sm"
          placeholder="Filter by email, name, or plan…"
          value={q}
          onChange={(e)=>setQ(e.target.value)}
        />
        <span className="text-xs font-mono" style={{color:"var(--ink-3)"}}>{filtered.length} / {users.length}</span>
      </div>

      {loading ? (
        <div className="text-sm" style={{ color: "var(--ink-3)" }}>Loading users…</div>
      ) : (
        <div className="card p-0 overflow-hidden" data-testid="admin-users-table">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left" style={{background:"var(--bg-soft)"}}>
                  {["Email","Name","Plan","Drafts","KB","Stories","POVs","Cmts","Posts","Joined","Actions"].map(h => (
                    <th key={h} className="px-4 py-3 eyebrow" style={{whiteSpace:"nowrap"}}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((u) => (
                  <tr key={u.id} data-testid={`admin-row-${u.email}`} className="align-middle" style={{borderTop:"1px solid var(--line)"}}>
                    <td className="px-4 py-3 font-mono text-xs">{u.email}</td>
                    <td className="px-4 py-3 truncate max-w-[140px]">{u.name || "—"}</td>
                    <td className="px-4 py-3">
                      <span className="chip" style={
                        u.plan === "pro" ? {background:"var(--accent-tint)", color:"var(--accent-2)", borderColor:"var(--accent)"} :
                        u.plan === "archived" ? {background:"var(--bg-soft)", color:"var(--ink-3)"} : {}
                      }>{u.plan || "—"}{u.role==="admin" && " · admin"}</span>
                    </td>
                    <td className="px-4 py-3 font-mono text-xs">{u.drafts_used || 0}</td>
                    <td className="px-4 py-3 font-mono text-xs">{u.kb_count || 0}</td>
                    <td className="px-4 py-3 font-mono text-xs">{u.story_count || 0}</td>
                    <td className="px-4 py-3 font-mono text-xs">{u.pov_count || 0}</td>
                    <td className="px-4 py-3 font-mono text-xs">{u.comment_count || 0}</td>
                    <td className="px-4 py-3 font-mono text-xs">{u.post_count || 0}</td>
                    <td className="px-4 py-3 font-mono text-xs" style={{color:"var(--ink-3)", whiteSpace:"nowrap"}}>
                      {u.created_at ? new Date(u.created_at).toLocaleDateString(undefined, {month:"short", day:"numeric", year:"numeric"}) : "—"}
                    </td>
                    <td className="px-4 py-3">
                      <select
                        data-testid={`admin-plan-select-${u.email}`}
                        value={u.plan || "trial"}
                        onChange={(e)=>updatePlan(u.email, e.target.value)}
                        disabled={busyEmail === u.email || u.role === "admin"}
                        className="select w-auto text-xs"
                        style={{padding:"6px 32px 6px 12px"}}
                        title={u.role === "admin" ? "Admin plan cannot be changed here" : ""}
                      >
                        <option value="trial">trial</option>
                        <option value="pro">pro</option>
                        <option value="archived">archived</option>
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
